import React, { useMemo } from "react";
import CommonModal from "@/components/common/CommonModal";
import type { ISalaryPreviewUser } from "@/interface/attendance.interface";
import { API_URL } from "@/api/constant";
import { handleFormatAmount } from "@/utility/helper";
import moment from "moment";
import {
  Calendar,
  Clock,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  Receipt,
  // FileSpreadsheet,
} from "lucide-react";

interface SalaryPreviewUserDetailModalProps {
  show: boolean;
  onClose: () => void;
  user: ISalaryPreviewUser | null;
  startDate?: string;
  endDate?: string;
}

const SalaryPreviewUserDetailModal: React.FC<SalaryPreviewUserDetailModalProps> = ({
  show,
  onClose,
  user,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  if (!user) return null;

  const {
    user: profile,
    attendanceSummary,
    totals,
    userSalary = [],
  } = user;

  const attendance = attendanceSummary?.attendance;
  const cycleDays = attendanceSummary?.totalDays ?? 0;
  const dailyRate = attendanceSummary?.dailyRate || 0;
  const absentDays = attendanceSummary?.absentDeductionDays ?? attendanceSummary?.absentDays ?? 0;
  const absentDeduction = attendanceSummary?.absentDeductionAmount ?? dailyRate * absentDays;
  const leaveDays = attendanceSummary?.leaveDeductionDays ?? attendanceSummary?.unpaidLeaveDays ?? 0;
  const leaveDeduction = attendanceSummary?.leaveDeductionAmount ?? dailyRate * leaveDays;
  const totalAttDeduction = attendanceSummary?.totalAttendanceDeductionAmount ?? (absentDeduction + leaveDeduction);

  const cycleStartDate = propStartDate || attendanceSummary?.availableStartDate;
  const cycleEndDate = propEndDate || attendanceSummary?.availableEndDate;

  const formattedPeriod = useMemo(() => {
    if (cycleStartDate && cycleEndDate) {
      return `${moment(cycleStartDate).format("DD MMM YYYY")} – ${moment(cycleEndDate).format("DD MMM YYYY")}`;
    }
    if (cycleStartDate) {
      return moment(cycleStartDate).format("DD MMM YYYY");
    }
    return null;
  }, [cycleStartDate, cycleEndDate]);

  const earningsHeads = userSalary.filter(
    (h) => (h.type || "").toLowerCase() === "earning"
  );
  const deductionHeads = userSalary.filter(
    (h) => (h.type || "").toLowerCase() === "deduction"
  );

  return (
    <CommonModal
      show={show}
      onClose={onClose}
      title=""
      className="sm:max-w-4xl max-w-4xl w-full p-0 overflow-hidden"
    >
      <div className="flex flex-col max-h-[85vh] bg-slate-50/50">
        {/* ── 1. Executive Header: Employee Profile & Quick Summary ─────────────── */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 p-5 sm:p-6 text-white shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {profile.profilePhoto ? (
                <img
                  src={`${API_URL}/${profile.profilePhoto}`}
                  alt={profile.name}
                  className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl object-cover border-2 border-white/20 shadow-md shrink-0"
                />
              ) : (
                <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl bg-indigo-500/20 text-white font-bold text-xl flex items-center justify-center border border-white/20 shadow-inner shrink-0">
                  {profile.name?.charAt(0)?.toUpperCase() || "E"}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white capitalize">
                    {profile.name}
                  </h3>
                  <span className="bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 text-xs font-mono font-bold px-2.5 py-0.5 rounded-md">
                    {profile.employeeCode || "N/A"}
                  </span>
                </div>
                <div className="text-xs text-slate-300 flex items-center gap-3 mt-1.5 flex-wrap">
                  {profile.email && <span className="text-slate-300">{profile.email}</span>}
                  {formattedPeriod && (
                    <span className="inline-flex items-center gap-1.5 bg-white/10 text-indigo-200 border border-white/15 px-2.5 py-0.5 rounded-full font-medium">
                      <Calendar className="w-3.5 h-3.5 text-indigo-300" />
                      {formattedPeriod} {cycleDays > 0 ? `(${cycleDays} Days)` : ""}
                    </span>
                  )}
                  {!formattedPeriod && cycleDays > 0 && (
                    <span className="inline-flex items-center gap-1 text-indigo-300 font-medium">
                      <Calendar className="w-3.5 h-3.5" />
                      Salary Cycle: {cycleDays} Days
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Net Salary KPI Box */}
            <div className="bg-emerald-950/60 border border-emerald-500/30 rounded-2xl px-5 py-2.5 text-left sm:text-right shrink-0">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400 block">
                Net Take-Home Salary
              </span>
              <span className="text-2xl sm:text-3xl font-black text-emerald-400">
                {handleFormatAmount(totals?.netSalary || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* ── 2. Modal Body (Clean Organized Sections) ────────────────────────── */}
        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          {/* Section A: Attendance & Working Days Statement */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center justify-between mb-3.5 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600" />
                <h4 className="text-xs sm:text-sm font-bold text-slate-800 uppercase tracking-wider">
                  Attendance & Working Days Summary
                </h4>
              </div>
              <div className="inline-flex items-center gap-1.5 text-xs text-slate-700 bg-slate-100/80 border border-slate-200 px-3 py-1 rounded-full font-medium">
                <span>Daily Wage Rate:</span>
                <span className="font-bold text-slate-900">{handleFormatAmount(dailyRate)} / day</span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2.5 text-center">
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-slate-500 font-medium block">Cycle Days</span>
                <span className="text-base font-bold text-slate-800">{cycleDays}</span>
              </div>
              <div className="bg-emerald-50 border border-emerald-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-emerald-700 font-medium block">Present</span>
                <span className="text-base font-bold text-emerald-700">{attendance?.present ?? 0}</span>
              </div>
              <div className="bg-amber-50 border border-amber-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-amber-700 font-medium block">Half Day</span>
                <span className="text-base font-bold text-amber-700">{attendance?.halfDay ?? 0}</span>
              </div>
              <div className="bg-blue-50 border border-blue-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-blue-700 font-medium block">Paid Leave</span>
                <span className="text-base font-bold text-blue-700">{attendanceSummary?.paidLeaveDays ?? 0}</span>
              </div>
              <div className="bg-rose-50 border border-rose-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-rose-700 font-medium block">Absent</span>
                <span className="text-base font-bold text-rose-700">{attendanceSummary?.absentDays ?? 0}</span>
              </div>
              <div className="bg-rose-50 border border-rose-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-rose-700 font-medium block">Unpaid Leave</span>
                <span className="text-base font-bold text-rose-700">{attendanceSummary?.unpaidLeaveDays ?? 0}</span>
              </div>
              <div className="bg-purple-50 border border-purple-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-purple-700 font-medium block">Holiday</span>
                <span className="text-base font-bold text-purple-700">{attendance?.holiday ?? 0}</span>
              </div>
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl py-2 px-1">
                <span className="text-[11px] text-slate-600 font-medium block">Week Off</span>
                <span className="text-base font-bold text-slate-700">{attendance?.weekOff ?? 0}</span>
              </div>
            </div>
          </div>

          {/* Section B: Loss of Pay (LOP) & Deduction Rules (Detailed Calculation Table) */}
          {(absentDays > 0 || leaveDays > 0 || totalAttDeduction > 0) && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4 text-rose-600" />
                <h4 className="text-xs sm:text-sm font-bold text-slate-800 uppercase tracking-wider">
                  Attendance Deduction / Loss of Pay (LOP) Breakdown
                </h4>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100/80 text-slate-600 font-semibold border-y border-slate-200">
                      <th className="py-2.5 px-3">Deduction Type</th>
                      <th className="py-2.5 px-3">Rule Status</th>
                      <th className="py-2.5 px-3">Calculation Formula</th>
                      <th className="py-2.5 px-3 text-right">Deduction Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    <tr>
                      <td className="py-2.5 px-3 font-semibold text-slate-800">
                        Absent Days Loss of Pay
                      </td>
                      <td className="py-2.5 px-3">
                        {attendanceSummary?.isAbsentDeductionApplied ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" /> Applied
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                            <XCircle className="w-3 h-3" /> Not Applied
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-600">
                        {handleFormatAmount(dailyRate)} × {absentDays} Days
                      </td>
                      <td className="py-2.5 px-3 text-right font-bold text-rose-600">
                        - {handleFormatAmount(absentDeduction)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-3 font-semibold text-slate-800">
                        Unpaid Leave Loss of Pay
                      </td>
                      <td className="py-2.5 px-3">
                        {attendanceSummary?.isLeaveDeductionApplied ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" /> Applied
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                            <XCircle className="w-3 h-3" /> Not Applied
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-600">
                        {handleFormatAmount(dailyRate)} × {leaveDays} Days
                      </td>
                      <td className="py-2.5 px-3 text-right font-bold text-rose-600">
                        - {handleFormatAmount(leaveDeduction)}
                      </td>
                    </tr>
                    <tr className="bg-rose-50/50 font-bold border-t border-rose-200 text-rose-900">
                      <td colSpan={3} className="py-2.5 px-3 text-right">
                        Total Attendance Deduction:
                      </td>
                      <td className="py-2.5 px-3 text-right font-black text-rose-700 text-sm">
                        - {handleFormatAmount(totalAttDeduction)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Section C: Salary Heads Ledger (Two-Column Standard Payslip Format) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Earnings Column */}
            <div className="bg-white border border-emerald-200 rounded-2xl overflow-hidden shadow-xs flex flex-col justify-between">
              <div>
                <div className="bg-emerald-50/80 px-4 py-3 border-b border-emerald-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <h4 className="text-xs sm:text-sm font-bold text-emerald-950 uppercase tracking-wider">
                      Earnings & Additions
                    </h4>
                  </div>
                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    {earningsHeads.length} Items
                  </span>
                </div>

                <div className="p-3 divide-y divide-slate-100">
                  {earningsHeads.length === 0 ? (
                    <div className="text-center py-6 text-xs text-slate-400">
                      No earning heads configured
                    </div>
                  ) : (
                    earningsHeads.map((head, idx) => (
                      <div
                        key={head.branchSalaryHeadId || head.id || idx}
                        className="py-2.5 px-1 flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">
                            {head.salaryName || head.name}
                          </span>
                          {head.isCustomAdded && (
                            <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-1.5 py-0.5 rounded border border-blue-200">
                              Custom
                            </span>
                          )}
                        </div>
                        <span className="font-bold text-emerald-700 text-sm">
                          {handleFormatAmount(head.amount || 0)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="bg-emerald-50/50 p-4 border-t border-emerald-200 flex items-center justify-between font-bold text-sm">
                <span className="text-slate-800">Total Gross Earnings:</span>
                <span className="text-emerald-700 text-base font-black">
                  {handleFormatAmount(totals?.earning || 0)}
                </span>
              </div>
            </div>

            {/* Deductions Column */}
            <div className="bg-white border border-rose-200 rounded-2xl overflow-hidden shadow-xs flex flex-col justify-between">
              <div>
                <div className="bg-rose-50/80 px-4 py-3 border-b border-rose-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-rose-600" />
                    <h4 className="text-xs sm:text-sm font-bold text-rose-950 uppercase tracking-wider">
                      Deductions & Recoveries
                    </h4>
                  </div>
                  <span className="text-[11px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
                    {deductionHeads.length + (totalAttDeduction > 0 ? 1 : 0)} Items
                  </span>
                </div>

                <div className="p-3 divide-y divide-slate-100">
                  {/* Attendance Loss item */}
                  {totalAttDeduction > 0 && (
                    <div className="py-2.5 px-1 flex items-center justify-between text-xs">
                      <div>
                        <span className="font-semibold text-rose-900 block">
                          Attendance Loss of Pay (LOP)
                        </span>
                        <span className="text-[11px] text-slate-500">
                          Absent ({absentDays}d) + Leave ({leaveDays}d)
                        </span>
                      </div>
                      <span className="font-bold text-rose-600 text-sm">
                        - {handleFormatAmount(totalAttDeduction)}
                      </span>
                    </div>
                  )}

                  {deductionHeads.map((head, idx) => (
                    <div
                      key={head.branchSalaryHeadId || head.id || idx}
                      className="py-2.5 px-1 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">
                          {head.salaryName || head.name}
                        </span>
                        {head.isCustomAdded && (
                          <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-1.5 py-0.5 rounded border border-blue-200">
                            Custom
                          </span>
                        )}
                      </div>
                      <span className="font-bold text-rose-600 text-sm">
                        - {handleFormatAmount(head.amount || 0)}
                      </span>
                    </div>
                  ))}

                  {deductionHeads.length === 0 && totalAttDeduction === 0 && (
                    <div className="text-center py-6 text-xs text-slate-400">
                      No deductions applied
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-rose-50/50 p-4 border-t border-rose-200 flex items-center justify-between font-bold text-sm">
                <span className="text-slate-800">Total Deductions:</span>
                <span className="text-rose-600 text-base font-black">
                  - {handleFormatAmount(totals?.deduction || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Section D: Final Net Salary Summary Banner */}
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-md">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-400/30 shrink-0">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs text-slate-300 font-medium block">
                  Net Salary Calculation Summary
                </span>
                <span className="text-xs sm:text-sm font-semibold text-slate-200">
                  Gross Earnings ({handleFormatAmount(totals?.earning || 0)}) − Total Deductions ({handleFormatAmount(totals?.deduction || 0)})
                </span>
              </div>
            </div>

            <div className="text-left sm:text-right shrink-0">
              <span className="text-xs text-emerald-300 font-medium block">
                Net Payable Amount
              </span>
              <span className="text-2xl sm:text-3xl font-black text-emerald-400">
                {handleFormatAmount(totals?.netSalary || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default SalaryPreviewUserDetailModal;
